import React, { useEffect, useRef, useState } from 'react'
import { Button, Text } from '@chakra-ui/react'

import Span from '~components/shared/texts/span'
import SharedInput from '~components/shared/form/shared-input'
import SharedStack from '~components/shared/wrappers/shared-stack'
import { validateEmail } from '~utils/helpers'
import { useEmailPasswordAuth } from '~utils/hooks/use-email-password-auth'
import MFAModal from './mfa'
import AuthErrorAlert from './auth-error-alert'

const RECAPTCHA_CONTAINER_ID = 'reauth-recaptcha-container'

interface Props {
    /**
     * Email of the locked user, shown read-only ("Signed in as") so they only
     * have to type their password. If empty (we couldn't determine who was
     * signed in) the email becomes an editable field, falling back to a full
     * sign-in.
     */
    email: string
    /**
     * Called once the user has successfully re-verified their identity (correct
     * password, plus MFA if enrolled). The screen should unlock in place,
     * keeping the page and any in-progress work mounted.
     */
    onReauthenticated: () => void
    /**
     * Escape hatch for "this isn't me": performs a full sign-out and sends the
     * user to the login page to sign in as someone else.
     */
    onUseDifferentAccount: () => void
}

/**
 * Compact re-authentication form used by the inactivity lock screen. The user
 * is still signed in (their session and page stay mounted underneath); entering
 * their password re-verifies them and unlocks the screen in place.
 */
export default function ReauthForm({
    email: lockedEmail,
    onReauthenticated,
    onUseDifferentAccount,
}: Props) {
    const { authError, setAuthError, signIn, mfa } =
        useEmailPasswordAuth(RECAPTCHA_CONTAINER_ID)

    const passwordInputRef = useRef<HTMLInputElement>(null)
    const isEmailLocked = !!lockedEmail
    const [email, setEmail] = useState<string>(lockedEmail)
    const [emailError, setEmailError] = useState<string | undefined>()
    const [password, setPassword] = useState<string>('')
    const [passwordError, setPasswordError] = useState<string | undefined>()
    const [isLoading, setIsLoading] = useState<boolean>(false)

    useEffect(() => {
        const timer = window.setTimeout(
            () => passwordInputRef.current?.focus(),
            50,
        )
        return () => window.clearTimeout(timer)
    }, [])

    function validateInputs() {
        if (!isEmailLocked && !validateEmail(email)) {
            setEmailError('Please enter a valid email address.')
            return false
        }
        if (!password) {
            setPasswordError('Please enter your password.')
            return false
        }
        return true
    }

    const handleSignIn = async () => {
        setIsLoading(true)
        setAuthError(undefined)
        if (!validateInputs()) {
            setIsLoading(false)
            return
        }
        const status = await signIn(email, password)
        if (status === 'success') {
            // Same user re-verified: unlock in place. The modal unmounts the
            // moment the lock clears, so we leave the button loading rather than
            // flashing it back to idle right before it disappears.
            onReauthenticated()
            return
        }
        setIsLoading(false)
    }

    return (
        <>
            <SharedStack spacing={4} w="full">
                <SharedInput
                    label="Signed in as"
                    type="email"
                    name="email"
                    autoComplete="username"
                    value={email}
                    isReadOnly={isEmailLocked}
                    tabIndex={isEmailLocked ? -1 : undefined}
                    bg={isEmailLocked ? 'gray.50' : undefined}
                    color={isEmailLocked ? 'gray.600' : undefined}
                    cursor={isEmailLocked ? 'not-allowed' : undefined}
                    containerProps={{ w: 'full' }}
                    onChange={e => setEmail(e.target.value)}
                    onBlur={() => setEmailError(undefined)}
                    error={emailError}
                />
                <SharedInput
                    ref={passwordInputRef}
                    label="Password"
                    type="password"
                    name="password"
                    autoComplete="current-password"
                    autoFocus
                    value={password}
                    containerProps={{ w: 'full' }}
                    onChange={e => setPassword(e.target.value)}
                    onBlur={() => setPasswordError(undefined)}
                    onKeyPress={e => {
                        if (e.key === 'Enter') {
                            handleSignIn()
                        }
                    }}
                    error={passwordError}
                />
                <AuthErrorAlert info={authError} />
                <Button
                    w="full"
                    colorScheme="brand"
                    isLoading={isLoading}
                    onClick={handleSignIn}
                >
                    Log in
                </Button>
                <Text fontSize="sm" color="gray.500" textAlign="center">
                    Not you?{' '}
                    <Span
                        color="brand.600"
                        fontWeight="medium"
                        cursor="pointer"
                        onClick={onUseDifferentAccount}
                    >
                        Sign in to a different account
                    </Span>
                </Text>
            </SharedStack>
            <div id={RECAPTCHA_CONTAINER_ID} />
            <MFAModal
                isOpen={mfa.isOpen}
                verificationId={mfa.verificationId}
                resolver={mfa.resolver}
                onVerified={onReauthenticated}
                onCancel={mfa.cancel}
            />
        </>
    )
}
